'use client';

/**
 * LatestWire — THE BRIEF's dense news column (brief.html §3 RIGHT + quiet-day §4).
 *
 * Replaces the fat breaking-news cards with the prototype's DENSE WIRE ROWS:
 *   kind label (mono) · one-line title · entity chip · relative time.
 * Header: `LATEST ON <CHAIN>` + `ALL NEWS →` (to the read page).
 *
 * DATA (2026-06-12 rework): rows are SERVER-FETCHED by the overview page
 * (`getChainWire` — one capped, index-free chain-wide read) and passed as plain
 * JSON props. The old per-tagId client fan-out (6 × /api/breaking-news) is GONE —
 * it only saw the top-6-by-articleCount entities, so a fresh DYLI/Anichess item
 * was invisible while the wire showed 3-day-old rows from the big-coverage tags
 * (Lam's "where's the latest breaking news?" bug). Server rows also mean the wire
 * is in the crawlable SSR HTML. This stays a client component ONLY for the
 * relative-time labels (post-mount — SSR/client HTML must agree).
 *
 * QUIET-DAY DEGRADATION (no-empty-boxes law): when there are 0 items fresh in the
 * last 24h, the header softens to `RECENTLY ON <CHAIN>` and a quiet notice row
 * leads — the rows then show the latest items WITHOUT pretending recency. Zero
 * items at all → a single honest "wire quiet" line, never an empty box.
 */
import { useEffect, useState } from 'react';
import Link from 'next/link';

/** Server-resolved tag → entity display, so a row's first known tag becomes an entity chip. */
export interface WireEntityLookup {
  [tagId: string]: { name: string; slug: string; handle?: string };
}

export interface WireItem {
  id: string;
  slug: string;
  title: string;
  publishedAt: string; // ISO
  tags: string[];
  isBreaking: boolean; // < 24h (server-computed at render time)
  /**
   * Row kind — the wire merges two sources with DIFFERENT routes:
   *   'breaking' → /breaking/<slug>/   'article' → its content-type route (/news,…).
   * Defaults to 'breaking' (the original wire was breaking-only). Drives both the
   * row link (`href`) and the kind label.
   */
  kind?: 'breaking' | 'article';
  /** Pre-resolved canonical route for the row. Falls back to /breaking/<slug>/ when absent. */
  href?: string;
}

const ROWS_SHOWN = 3;

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function LatestWire({
  chainLabel,
  hubSlug,
  items,
  entityLookup,
}: {
  chainLabel: string;
  hubSlug: string;
  /** Chain-wide published items, freshest event first (server-fetched). */
  items: WireItem[];
  entityLookup: WireEntityLookup;
}) {
  // Relative datelines compare against the wall clock — render post-mount only
  // (the rows themselves are in the SSR HTML; only the "2h ago" labels wait).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const shown = items.slice(0, ROWS_SHOWN);
  const anyFresh = shown.some((i) => i.isBreaking);
  // Header tone: when nothing is fresh in 24h, don't pretend recency.
  const quiet = !anyFresh;
  const headerLabel = quiet ? `Recently on ${chainLabel}` : `Latest on ${chainLabel}`;

  function entityFor(item: WireItem): { name: string; slug: string; handle?: string } | undefined {
    for (const t of item.tags) {
      const hit = entityLookup[t];
      if (hit) return hit;
    }
    return undefined;
  }

  return (
    <div className="flex flex-col overflow-hidden rounded border-[0.5px] border-divider bg-surface">
      {/* header */}
      <div className="flex items-center justify-between border-b-[0.5px] border-divider px-5 py-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-parchment-dim">
          {headerLabel}
        </span>
        <Link
          href={`/${hubSlug}/news`}
          className="font-mono text-[10px] uppercase tracking-[0.08em] text-scout-gold transition-opacity hover:opacity-80"
        >
          All news →
        </Link>
      </div>

      {/* quiet-day notice — only when there are rows but nothing fresh */}
      {quiet && shown.length > 0 && (
        <div className="flex items-center gap-2.5 border-b-[0.5px] border-divider bg-white/[0.015] px-5 py-3 font-mono text-[10px] tracking-[0.05em] text-slate-light">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full border border-slate-ds" aria-hidden />
          No breaking flags in the last 24h — showing the latest from the record.
        </div>
      )}

      {/* rows */}
      {shown.length > 0 && (
        <div className="flex flex-col">
          {shown.map((item) => {
            const ent = entityFor(item);
            const isArticle = item.kind === 'article';
            // Article rows always read as ARTICLE; breaking rows keep the fresh/stale split.
            const kindLabel = isArticle ? 'Article' : item.isBreaking ? 'Breaking' : 'News';
            // Article rows are never the red live-breaking accent.
            const accentBreaking = !isArticle && item.isBreaking;
            return (
              <Link
                key={item.id}
                href={item.href ?? `/breaking/${item.slug}`}
                className="grid grid-cols-[72px_1fr_auto] items-center gap-4 border-b-[0.5px] border-divider px-5 py-4 transition-colors last:border-b-0 hover:bg-white/[0.02]"
              >
                <span
                  className={`font-mono text-[9px] font-bold uppercase tracking-[0.1em] ${
                    accentBreaking ? 'text-alert-red-text' : 'text-slate-light'
                  }`}
                >
                  {kindLabel}
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-sm font-semibold leading-snug text-parchment line-clamp-1">
                    {item.title}
                  </span>
                  {ent && (
                    <span className="mt-1 inline-flex items-center gap-1.5 font-mono text-[10px] text-parchment-dim">
                      <span className="h-[5px] w-[5px] rounded-full bg-scout-gold" aria-hidden />
                      {ent.name}
                      {ent.handle ? ` · @${ent.handle}` : ''}
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap font-mono text-[10px] text-parchment-dim/70">
                  {hasMounted && item.publishedAt ? relativeTime(item.publishedAt) : ''}
                </span>
              </Link>
            );
          })}
        </div>
      )}

      {/* total dead-end — zero items: a single honest line (never an empty box) */}
      {shown.length === 0 && (
        <div className="flex items-center gap-2.5 px-5 py-5 font-mono text-[10px] tracking-[0.05em] text-slate-light">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full border border-slate-ds" aria-hidden />
          No published news yet for {chainLabel} — the wire lights up as the desk files.
        </div>
      )}
    </div>
  );
}
